import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useCartContext } from '@/resources/hooks/useCartContext';
import { useViewCart } from '@/resources/hooks/useViewCart';
import { getShippingDiscounts } from '@/routes/integrations';
import { getFreeShippingProducts } from '@/routes/products';
import { getOrderMinValue } from '@/services/global';
import * as PaymentActions from '@/store/ducks/payment/actions';
import { IPaymentAlertAsks } from '@/store/ducks/payment/types';

interface UseCartEffectsProps {
  order: any;
  paymentAlertAsks: IPaymentAlertAsks;
  setShowCartDeliveryAlert: (show: boolean) => void;
}

export const useCartEffects = ({
  order,
  paymentAlertAsks,
  setShowCartDeliveryAlert,
}: UseCartEffectsProps) => {
  const dispatch = useDispatch();
  const { min_order_amount, webapp_out_of_delivery } = useCartContext();

  useEffect(() => {
    if (!order?.shipment) return;

    if (
      Number(order?.cart.items_total) < getOrderMinValue(order, min_order_amount) &&
      ((order.shipment.type === 'delivery' && webapp_out_of_delivery) || webapp_out_of_delivery)
    ) {
      setShowCartDeliveryAlert(true);
    } else {
      setShowCartDeliveryAlert(false);
    }
  }, [
    order?.cart.items_total,
    order?.shipment?.delivery?.id,
    order?.dst_shop_id,
    min_order_amount,
    webapp_out_of_delivery,
    setShowCartDeliveryAlert,
  ]);

  useEffect(() => {
    if (!order?.cart || !order?.cart.products?.length) return;

    useViewCart(order.cart);
  }, [order?.id]);

  useEffect(() => {
    if (!order) return;

    const validPayments = order.payments?.filter?.((payment: any) => !payment.deleted_at);
    const isPaid = validPayments?.length
      ? validPayments?.every((p: any) => p.status === 'paid')
      : false;

    const hasToShowModal = validPayments && validPayments.length > 0 && isPaid;

    if (!paymentAlertAsks.has_shown_paid_alert && hasToShowModal) {
      dispatch(PaymentActions.setShowPaidAlert(true));
    }

    getShippingDiscounts(order.dst_shop_id);
  }, [order?.id, order?.dst_shop_id, dispatch, paymentAlertAsks.has_shown_paid_alert]);

  useEffect(() => {
    if (
      !order ||
      !order?.cart.delivery_tax ||
      (order.shipment.type === 'delivery' && !order?.cart.delivery_tax)
    ) {
      return;
    }

    getFreeShippingProducts(order.dst_shop_id);
  }, [order?.id, order?.dst_shop_id, order?.cart.delivery_tax, order?.shipment?.type]);
};
